/**
 * The backlog filter preview: what a Team's backlog filter reads as, checked
 * against the tracker while a person types it (`POST /api/desks/:id/backlog/preview`,
 * `POST /api/desks/backlog/preview` for a Team being created).
 *
 * The filter is shown exactly as written and never paraphrased: on 2026-10-09
 * a pasted `label = yagni` was rewritten into prose, Jira rejected it (the
 * field is `labels`), and the Team worked from nothing. The preview says what
 * the tracker answers instead: a count, or the field it refused with a fix.
 */
import { collapseJql, parseTrackerLink, stripJqlLiterals } from "./trackerLinks.js";
/** Rows a preview shows. */
export const BACKLOG_PREVIEW_ROWS = 5;
/** The longest filter a preview reads. */
export const BACKLOG_PREVIEW_INPUT_MAX = 2048;
/** The most pasted keys one preview resolves. */
export const BACKLOG_PREVIEW_KEYS_MAX = 100;
/**
 * Fields people type that Jira does not know, and the field they meant. Keys
 * are lower-case; the value is what Jira takes.
 */
export const JIRA_FIELD_SUGGESTIONS = {
    label: "labels",
    epic: '"Epic Link"',
    project_key: "project",
    status_category: "statusCategory",
    assignee_name: "assignee",
};
export function jiraFieldSuggestion(field) {
    return JIRA_FIELD_SUGGESTIONS[field.trim().toLowerCase()] ?? null;
}
/**
 * The field a Jira 400 names as unknown: "Field 'label' does not exist or you
 * do not have permission to view it." Null for any other complaint.
 */
export function jiraUnknownField(message) {
    const match = /field\s+['"]([^'"]{1,100})['"]\s+does not exist/i.exec(message);
    return match?.[1]?.trim() || null;
}
const ISSUE_KEY = /^[A-Za-z][A-Za-z0-9_]*-\d+$/;
/** A project key as people type one: upper-case, two to sixteen characters. */
const TYPED_PROJECT_KEY = /^[A-Z][A-Z0-9_]{1,15}$/;
/** JQL that ends on a word that needs something after it. */
const DANGLING_TAIL = /(?:!=|!~|>=|<=|=|~|>|<|,|\(|\b(?:and|or|not|in|is|was|order|by|changed)\b)\s*$/i;
/**
 * Whether JQL is still being typed: an open quote or bracket, or a tail that
 * needs a value (`labels =`, `project in (`, `ORDER BY`). A preview does not
 * send an unfinished query to the tracker.
 */
export function jqlLooksUnfinished(text) {
    let quote = null;
    let depth = 0;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (quote) {
            if (ch === "\\")
                i += 1;
            else if (ch === quote)
                quote = null;
            continue;
        }
        if (ch === '"' || ch === "'")
            quote = ch;
        else if (ch === "(")
            depth += 1;
        else if (ch === ")")
            depth -= 1;
    }
    if (quote || depth > 0)
        return true;
    return DANGLING_TAIL.test(stripJqlLiterals(text).trim());
}
/** A pasted list of issue keys (`NMT-1, NMT-2` or one per line), upper-cased and de-duplicated. */
function issueKeysIn(text) {
    const parts = text.split(/[\s,;]+/).filter(Boolean);
    if (parts.length === 0 || !parts.every((part) => ISSUE_KEY.test(part)))
        return null;
    return [...new Set(parts.map((part) => part.toUpperCase()))];
}
/**
 * Read a typed backlog filter without asking any tracker. `source` is the
 * tracker the person picked, when they picked one; a bare key list or JQL
 * defaults to Jira.
 */
export function classifyBacklogFilter(input, source) {
    const text = input.trim();
    if (!text)
        return { kind: "empty" };
    const keys = issueKeysIn(text);
    if (keys) {
        if (source === "linear") {
            return { kind: "unsupported", message: "A list of Linear tickets is not a backlog. Paste a Linear view link instead." };
        }
        return { kind: "keys", keys };
    }
    const isLink = /^https?:\/\//i.test(text) || /^[a-z0-9.-]+\.(atlassian\.net|linear\.app)\//i.test(text);
    const target = parseTrackerLink(text);
    if (isLink) {
        if (!target)
            return { kind: "unsupported", message: "That link is not a Jira filter, Jira project or Linear view." };
        if (target.source === "linear") {
            if (target.kind === "view")
                return { kind: "linear_view", slugId: target.slugId, workspace: target.workspace ?? null };
            return { kind: "unsupported", message: "Only a Linear saved view is previewed here. Paste the view's link." };
        }
        if (target.source === "jira") {
            if (target.kind === "query") {
                if (target.jql)
                    return jqlReading(target.jql);
                if (target.filterId)
                    return { kind: "filter_link", filterId: target.filterId };
                return {
                    kind: "unsupported",
                    message: "That list depends on who is looking. Paste a saved filter link or the JQL instead.",
                };
            }
            if (target.kind === "project")
                return { kind: "filter_link", projectKey: target.key };
            return { kind: "issue_link", key: target.key.toUpperCase() };
        }
        return { kind: "unsupported", message: "That link is not a Jira filter, Jira project or Linear view." };
    }
    if (source === "linear") {
        return { kind: "unsupported", message: "Linear has no query language. Paste a Linear view link instead." };
    }
    // A single word: a project key typed in capitals, or the start of a query.
    if (!/\s/.test(text)) {
        if (TYPED_PROJECT_KEY.test(text))
            return { kind: "filter_link", projectKey: text };
        return /[=~<>]/.test(text) ? jqlReading(text) : { kind: "partial" };
    }
    if (jqlLooksUnfinished(text))
        return { kind: "partial" };
    return jqlReading(text);
}
function jqlReading(jql) {
    const collapsed = collapseJql(jql);
    if (!collapsed)
        return { kind: "empty" };
    return jqlLooksUnfinished(collapsed) ? { kind: "partial" } : { kind: "jql", jql: collapsed };
}
//# sourceMappingURL=backlogPreview.js.map